import {Button} from '@primer/react'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

/**
 * Payload produced by the monolith billing model
 * `Billing::Notifications::CombinedBudgetThresholdBanner#to_payload_hash`.
 * Reused verbatim so the React side renders the billing team's existing
 * budget-threshold banner contract rather than duplicating copy.
 */
export interface BudgetThresholdBannerPayload {
  text: string
  variant: 'critical' | 'warning'
  /**
   * Mirrors the monolith payload field. The monolith
   * (`Billing::Notifications::CombinedBudgetThresholdBanner#dismissible?`)
   * hardcodes this to `true`, so the banner is always dismissible and
   * `IpmBannerBase` always renders the dismiss button. The field is kept for
   * payload parity but intentionally not branched on here.
   */
  dismissible: boolean
  dismiss_link: string
  budget_id: string
  threshold: number | null
}

export interface BudgetThresholdBannerAnalytics {
  category: string
  location: string
  threshold: number | null
  variant: string
  visibleEvent: string
  dismissEvent: string
  clickEvent: string
}

export interface BudgetThresholdBannerProps {
  banner: BudgetThresholdBannerPayload
  ctaHref: string
  analytics: BudgetThresholdBannerAnalytics
  rounded?: boolean
}

export function BudgetThresholdBanner({banner, ctaHref, analytics, rounded}: BudgetThresholdBannerProps) {
  const isGlobal = (rounded ?? true) === false || analytics.location === 'global'

  return (
    <IpmBannerBase
      dismissPath={banner.dismiss_link}
      visibleEvent={analytics.visibleEvent}
      dismissEvent={analytics.dismissEvent}
      testId="budget-threshold-banner"
      title="Billing budget alert"
      variant={banner.variant}
      location={analytics.location}
      rounded={rounded}
      description={<span className="text-normal">{banner.text}</span>}
      primaryAction={
        <Button as="a" href={ctaHref} size={isGlobal ? 'small' : 'medium'} data-analytics-event={analytics.clickEvent}>
          Manage budgets
        </Button>
      }
    />
  )
}
